'use client'

import { cn } from '@/lib/utils'

import { EASE, HAIRLINE, MODULES, SectionHead, useReveal } from './shared'

/* Six modules physically wired to one line. The shared template renders this
   idea as six equal disconnected rectangles in a `lg:grid-cols-3` — which is
   the literal opposite of "everything under one roof". Here the wiring is the
   section, and it rotates from horizontal to vertical on phones rather than
   collapsing into a card stack.

   Every icon on this page lives here, and every one of them is a bare glyph
   demoted to the corner of its cell: no tinted rounded square, no ring, no
   container div. The name and the sentence carry the cell. */

/** Half a column of a six-column grid — where the bus must stop so it lands
    exactly on the outer drops rather than running past them. */
const HALF_COL = '8.3333%'

export function UdcBus() {
  const wiring = useReveal<HTMLDivElement>(0.15)

  return (
    <section className="pt-16 sm:pt-20 md:pt-28">
      <div className="container mx-auto">
        <SectionHead
          eyebrow="The architecture"
          title="Six modules. One datastore."
          deck="Integration is not a feature bolted on afterwards. The modules were never separate systems: they read and write the same records, under one identity and one audit trail."
        />

        <div ref={wiring.ref} className="mt-10 sm:mt-12">
          {/* ══ lg+ : cells across the top, wired down to a single bus ══ */}
          <div className="hidden lg:block">
            <div className="grid grid-cols-6 gap-4">
              {MODULES.map((m, i) => (
                <Cell key={m.key} module={m} index={i} on={wiring.shown} />
              ))}
            </div>

            {/* Wiring strip. Drops sit at the centre of each grid column, so
                they stay locked to the cells at any container width. */}
            <div className="relative h-20">
              <div aria-hidden className="grid h-full grid-cols-6">
                {MODULES.map((m, i) => (
                  <div key={m.key} className="relative">
                    <span
                      className="absolute left-1/2 top-0 h-1/2 w-px -translate-x-1/2 origin-top bg-border/70"
                      style={{
                        transform: wiring.shown ? 'scaleY(1)' : 'scaleY(0)',
                        transition: `transform 420ms ${EASE} ${i * 70}ms`,
                      }}
                    />
                    {/* Junction node, in the hero circuit's vocabulary:
                        hollow, filled with the page background. */}
                    <span
                      className="absolute left-1/2 top-1/2 size-[13px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-emerald-600/40 bg-background dark:border-primary-active/30"
                      style={{
                        opacity: wiring.shown ? 1 : 0,
                        transition: `opacity 300ms ${EASE} ${260 + i * 70}ms`,
                      }}
                    />
                  </div>
                ))}
              </div>

              {/* The bus itself */}
              <span
                aria-hidden
                className="absolute top-1/2 h-px origin-left bg-emerald-600/40 dark:bg-primary-active/30"
                style={{
                  left: HALF_COL,
                  right: HALF_COL,
                  transform: wiring.shown ? 'scaleX(1)' : 'scaleX(0)',
                  transition: `transform 900ms ${EASE} 420ms`,
                }}
              />

              {/* Drop from the bus into the datastore label */}
              <span
                aria-hidden
                className="absolute left-1/2 top-1/2 h-1/2 w-px -translate-x-1/2 origin-top bg-emerald-600/40 dark:bg-primary-active/30"
                style={{
                  transform: wiring.shown ? 'scaleY(1)' : 'scaleY(0)',
                  transition: `transform 380ms ${EASE} 1180ms`,
                }}
              />
            </div>

            <DatastoreLabel on={wiring.shown} delay={1420} />
          </div>

          {/* ══ < lg : the same diagram, rotated ══ */}
          <div className="lg:hidden">
            <div className="relative pl-7 sm:pl-9">
              {/* vertical bus */}
              <span
                aria-hidden
                className="absolute bottom-6 left-[6px] top-6 w-px origin-top bg-emerald-600/40 dark:bg-primary-active/30 sm:left-2"
                style={{
                  transform: wiring.shown ? 'scaleY(1)' : 'scaleY(0)',
                  transition: `transform 900ms ${EASE} 120ms`,
                }}
              />

              <div className="space-y-3">
                {MODULES.map((m, i) => (
                  <div key={m.key} className="relative">
                    {/* spur + node */}
                    <span
                      aria-hidden
                      className="absolute left-[-21px] top-6 h-px w-[15px] origin-left bg-border/70 sm:left-[-25px] sm:w-[19px]"
                      style={{
                        transform: wiring.shown ? 'scaleX(1)' : 'scaleX(0)',
                        transition: `transform 380ms ${EASE} ${240 + i * 80}ms`,
                      }}
                    />
                    <span
                      aria-hidden
                      className="absolute left-[-27px] top-6 size-[13px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-emerald-600/40 bg-background sm:left-[-27px] dark:border-primary-active/30"
                      style={{
                        opacity: wiring.shown ? 1 : 0,
                        transition: `opacity 300ms ${EASE} ${240 + i * 80}ms`,
                      }}
                    />
                    <Cell module={m} index={i} on={wiring.shown} />
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <DatastoreLabel on={wiring.shown} delay={860} />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function Cell({
  module: m,
  index,
  on,
}: {
  module: (typeof MODULES)[number]
  index: number
  on: boolean
}) {
  const Glyph = m.icon

  return (
    <div
      className={cn(
        'relative rounded-2xl border px-4 py-4 sm:px-5 sm:py-5',
        HAIRLINE,
        'bg-background'
      )}
      style={{
        opacity: on ? 1 : 0,
        transform: on ? 'none' : 'translateY(10px)',
        transition: `opacity 480ms ${EASE} ${index * 70}ms, transform 480ms ${EASE} ${index * 70}ms`,
      }}
    >
      <div className="font-heading pr-6 text-base font-normal leading-tight text-foreground">
        {m.name}
      </div>
      <div className="mt-2 pr-6 text-[11px] leading-snug text-muted-foreground sm:text-xs">
        {m.line}
      </div>

      {/* Demoted to a footnote in the corner. This is the whole icon
          treatment on the page. */}
      <Glyph
        aria-hidden
        className="absolute bottom-4 right-4 size-5 text-foreground/40 sm:bottom-5 sm:right-5"
      />
    </div>
  )
}

function DatastoreLabel({ on, delay }: { on: boolean; delay: number }) {
  return (
    <div
      className="flex justify-center"
      style={{
        opacity: on ? 1 : 0,
        transform: on ? 'none' : 'translateY(6px)',
        transition: `opacity 500ms ${EASE} ${delay}ms, transform 500ms ${EASE} ${delay}ms`,
      }}
    >
      <div
        className={cn(
          'rounded-full border px-5 py-2.5 text-center',
          'border-emerald-600/30 dark:border-primary-active/25',
          'bg-emerald-600/[0.05] dark:bg-primary-active/[0.05]'
        )}
      >
        <span className="text-[11px] uppercase tracking-[0.14em] text-emerald-600 sm:text-xs dark:text-primary-active">
          One datastore · one identity · one audit trail
        </span>
      </div>
    </div>
  )
}
